import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "src/_common/hooks/useTranslation";

interface SetValidityFormData {
  hours: number | string;
}

interface SetValidityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (hours: number) => void;
}

const SetValidityModal: React.FC<SetValidityModalProps> = ({ isOpen, onClose, onSubmit: onSubmitCallback }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation("job-details");

  // Validation schema
  const validitySchema = yup.object().shape({
    hours: yup
      .number()
      .required(t("jobDetails.screening.setValidityModal.validation.required"))
      .positive(t("jobDetails.screening.setValidityModal.validation.positive"))
      .integer(t("jobDetails.screening.setValidityModal.validation.integer"))
      .max(168, t("jobDetails.screening.setValidityModal.validation.max"))
      .typeError(t("jobDetails.screening.setValidityModal.validation.typeError")),
  });

  // Initialize React Hook Form
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SetValidityFormData>({
    resolver: yupResolver(validitySchema),
    defaultValues: {
      hours: "",
    },
    reValidateMode: "onChange",
  });

  // Handle form submission
  const onSubmit = async (data: SetValidityFormData) => {
    setIsSubmitting(true);
    const hours = Number(data.hours);

    // Call the parent's onSubmit callback
    onSubmitCallback(hours);

    // Reset form and close modal
    handleClose();
    setIsSubmitting(false);
  };

  // Handle modal close
  const handleClose = () => {
    if (isSubmitting) return; // Prevent closing while submitting

    reset({
      hours: "",
    });
    onClose();
  };

  // Handle overlay click
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Don't render if modal is not open
  if (!isOpen) {
    return null;
  }

  return (
    <div className="currency-modal-overlay" onClick={handleOverlayClick}>
      <div className="currency-modal" onClick={(e) => e.stopPropagation()}>
        <div className="currency-modal-header">
          <h3 className="currency-head">{t("jobDetails.screening.setValidityModal.title")}</h3>
        </div>

        <div className="ai-description-content">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="modal-invite-wrap">
              {/* Hours Field */}
              <div className="drjob-form-group">
               

                {/* Informational Section */}
                <div className="alert alert-info mb-3" style={{ 
                  backgroundColor: '#e7f3ff', 
                  borderLeft: '4px solid #2196F3',
                  padding: '15px 20px',
                  borderRadius: '4px',
                  marginBottom: '20px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <i className="fa fa-info-circle" style={{ 
                      fontSize: '20px', 
                      color: '#2196F3', 
                      marginRight: '12px',
                      marginTop: '2px'
                    }}></i>
                    <div>
                      <strong style={{ color: '#1976D2', display: 'block', marginBottom: '8px' }}>
                        {t("jobDetails.screening.setValidityModal.policyTitle")}
                      </strong>
                      <p style={{ margin: 0, color: '#555', lineHeight: '1.6' }}>
                        {t("jobDetails.screening.setValidityModal.policyDescription")}
                      </p>
                    </div>
                  </div>
                </div>
                 <label className="form-label">
                  {t("jobDetails.screening.setValidityModal.hours")} <span className="mandatory-mark">*</span>
                </label>
                <Controller
                  name="hours"
                  control={control}
                  render={({ onChange, onBlur, value, name, ref }) => (
                    <input
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value}
                      name={name}
                      ref={ref}
                      type="number"
                      placeholder={t("jobDetails.screening.setValidityModal.placeholder")}
                      className={`form-control w-100 ${errors.hours ? "is-invalid" : ""}`}
                      disabled={isSubmitting}
                      autoFocus
                    />
                  )}
                />
                {errors.hours && (
                  <div className="invalid-feedback" style={{ display: "block" }}>
                    {errors.hours.message}
                  </div>
                )}
               
              </div>
            </div>

            {/* Action Buttons - Only Submit */}
            <div className="modal-invite-actions">
              <button type="submit" className="btn-preview" disabled={isSubmitting}>
                {isSubmitting ? t("jobDetails.screening.setValidityModal.submitting") : t("jobDetails.screening.setValidityModal.submit")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SetValidityModal;
